import z from "zod";

const translationPath = "page.4uBus.register.iService.errors.";

const companyDigitalCertificateSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    enable: z.boolean(),
  },
  { invalid_type_error: translationPath + "company_digital_certificate" },
);

const uBusUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.number().nullish(),
});

const iServiceTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.number().nullish(),
});

const methodTypeSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().nullish(),
  },
  { invalid_type_error: translationPath + "method_type" },
);

const si_pniSchema = z
  .object({
    authUrl: z.string(),
    baseUrl: z.string(),
    responsibleDocument: z.string().min(1, translationPath + "cpf"),
    companyDigitalCertificate: companyDigitalCertificateSchema.nullish(),
  })
  .superRefine((val, ctx) => {
    const urlRegex = /^https?:\/\/.+/i;

    if (!urlRegex.test(val.authUrl.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["authUrl"],
        message: translationPath + "url",
      });
    }
    if (!urlRegex.test(val.baseUrl.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["baseUrl"],
        message: translationPath + "url",
      });
    }
    if (!val.companyDigitalCertificate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["companyDigitalCertificate"],
        message: translationPath + "company_digital_certificate",
      });
    }
  });

const certillionSchema = z
  .object({
    companyDigitalCertificate: companyDigitalCertificateSchema.nullish(),
    isCertillionCloud: z.boolean(),
    authorizeLocal: z.boolean(),
    baseUrl: z.string().min(1, translationPath + "required_field"),
    baseUrlProxy: z.string().min(1, translationPath + "required_field"),
    clientId: z.string().min(1, translationPath + "required_field"),
    clientSecret: z.string().min(1, translationPath + "required_field"),
    signatureHorizontalX: z.string().nullish(),
    signatureHorizontalY: z.string().nullish(),
    signatureVerticalX: z.string().nullish(),
    signatureVerticalY: z.string().nullish(),
    signatureStandardOptions: z
      .string()
      .min(1, translationPath + "required_field"),
  })
  .superRefine((val, ctx) => {
    const urlRegex = /^https?:\/\/.+/i;

    if (!urlRegex.test(val.baseUrl.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["baseUrl"],
        message: translationPath + "url",
      });
    }
    if (!urlRegex.test(val.baseUrlProxy.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["baseUrlProxy"],
        message: translationPath + "url",
      });
    }

    try {
      if (val.signatureStandardOptions) {
        JSON.parse(val.signatureStandardOptions);
      }
    } catch (e) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["signatureStandardOptions"],
        message: translationPath + "invalid_json",
      });
    }
  });

const busInternalTokenSchema = z
  .object({
    uBusUserExternal: z.boolean().nullish(),
    busInternalToken: uBusUserSchema.nullish(),
    token: z.string().nullish(),
    host: z.string().nullish(),
  })
  .superRefine((val, ctx) => {
    if (!val.uBusUserExternal) {
      if (!val.busInternalToken) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["busInternalToken"],
          message: translationPath + "bus_internal_token",
        });
      }
      return;
    }
    const urlRegex = /^https?:\/\/.+/i;

    if (!val.token) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["token"],
        message: translationPath + "token",
      });
    }
    if (!val.host) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["host"],
        message: translationPath + "host",
      });
    } else if (!urlRegex.test(val.host.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["host"],
        message: translationPath + "url",
      });
    }
  });

const gerAcessoSchema = z
  .object({
    token: z.string().min(1, translationPath + "token"),
    baseUrl: z.string().min(1, translationPath + "url"),
  })
  .superRefine((val, ctx) => {
    const urlRegex = /^https?:\/\/.+/i;

    if (!urlRegex.test(val.baseUrl.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["baseUrl"],
        message: translationPath + "url",
      });
    }
  });

const plugNotasSchema = z
  .object({
    baseUrl: z.string().min(1, translationPath + "url"),
    token: z.string().min(1, translationPath + "token"),
    defaultProviderDocument: z
      .string()
      .min(1, translationPath + "required_field"),
  })
  .superRefine((val, ctx) => {
    const urlRegex = /^https?:\/\/.+/i;

    if (!urlRegex.test(val.baseUrl.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["baseUrl"],
        message: translationPath + "url",
      });
    }
  });

const underDevelopmentServiceSchema = z.record(z.any()).nullish();

export const iServiceSchema = z
  .object({
    id: z.string().optional(),
    enable: z.boolean(),
    name: z.string().min(1, translationPath + "name"),
    description: z.string().nullish(),
    methodType: methodTypeSchema,
    iServiceType: iServiceTypeSchema.nullish(),
    configs: z.string().nullish(),
    uBusUser: uBusUserSchema.nullish(),
    uBusUserExternal: z.boolean().nullish(),
    busInternalToken: busInternalTokenSchema.nullish(),
    companyDigitalCertificate: companyDigitalCertificateSchema.nullish(),
    si_pni: si_pniSchema.nullish(),
    certillion: certillionSchema.nullish(),
    gerAcesso: gerAcessoSchema.nullish(),
    plugNotas: plugNotasSchema.nullish(),
    shiftLis: underDevelopmentServiceSchema,
    protheus: underDevelopmentServiceSchema,
    sgu: underDevelopmentServiceSchema,
  })
  .superRefine((val, ctx) => {
    if (!val.iServiceType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["iServiceType"],
        message: translationPath + "i_service_type",
      });
    }
  });

export type TIServiceForm = z.infer<typeof iServiceSchema>;
